import React, { useState, useRef, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faFilter, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Button } from 'reactstrap'
import useTableFilters from 'helpers/useTableFilters';
import BaseTable from '../shared/BaseTable';
import FilterSelect from '../shared/FilterSelect';
import { headerOperation, cellOperation } from '../../helpers/tables';
import { API, BASE_URL, NO_FILTERED_ID } from '../../CONST';
import { formatRut } from '../../helpers/helpers';
import { get } from '../../helpers/requests';

const FilterInput = React.memo(
  ({ columnId, placeholder, onChange, minLength = 3, clearSignal }) => {
    const [value, setValue] = useState('');
    const [showTooltip, setShowTooltip] = useState(false);

    useEffect(() => {
      setValue('');
    }, [clearSignal]);

    const handleChange = (e) => {
      const newValue = e.target.value;
      setValue(newValue);
      onChange(columnId, newValue);
    };

    return (
      <div style={{ position: 'relative' }}>
        <input
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onFocus={() => minLength > 0 && setShowTooltip(true)}
          onBlur={() => setShowTooltip(false)}
          style={{ width: '100%' }}
        />
      </div>
    );
  }
);

FilterInput.propTypes = {
  columnId: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  minLength: PropTypes.number.isRequired,
  clearSignal: PropTypes.number.isRequired,
};

const FilterSelectWrapper = ({ columnId, options, defaultOption, onChange, clearSignal }) => {
  const [localValue, setLocalValue] = useState(null);

  useEffect(() => {
    setLocalValue(null);
    onChange(columnId, null);
  }, [clearSignal, columnId, onChange])

  const handleChange = (value) => {
    setLocalValue(value);
    onChange(columnId, value === NO_FILTERED_ID ? null : value);
  };

  const fakeFilter = localValue !== null ? { id: columnId, value: localValue } : null;

  return (
    <FilterSelect
      filter={fakeFilter}
      defaultOption={defaultOption}
      onFilterChange={handleChange}
      options={options}
    />
  );
}

FilterSelectWrapper.propTypes = {
  columnId: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  defaultOption: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  clearSignal: PropTypes.number.isRequired,
};

const DeudorListTable = () => {
  const [onFilteredChange, onFetchData, loading, data, getData] = useTableFilters(
    `${BASE_URL}${API.deudores}`,
  );
  const [paisesOptions, setPaisesOptions] = useState([]);
  const [clearSignal, setClearSignal] = useState(0);

  const [activeFilters, setActiveFilters] = useState([]);
  const [filterCount, setFilterCount] = useState(0);
  const tableStateRef = useRef({});
  const isInitialMount = useRef(true);
  
  const filterValuesRef = useRef({});
  
  useEffect(() => {
    get(`${BASE_URL}${API.paises}?con_deudores=true`).then((response) => {
      const options = response.map((pais) => ({
        label: pais.name,
        value: pais.id,
      }));
      setPaisesOptions(options);
    });
  }, []);

  const handleFilterChange = (columnId, value) => {
    filterValuesRef.current[columnId] = value;
  };

  const columns = useMemo(() => {
    const createFilterInput = (filterKey, placeholder, minLength = 3) => () => (
      <FilterInput
        columnId={filterKey}
        placeholder={placeholder}
        onChange={handleFilterChange}
        minLength={minLength}
        clearSignal={clearSignal}
      />
    );

    return [
      {
        Header: headerOperation('N° Documento', 'center'),
        id: 'rut__icontains',
        accessor: 'rut',
        Cell: (row) => {
          const countryCode = row.original.pais?.code ? row.original.pais.code : 'CL';

          return cellOperation(countryCode === 'CL' ? formatRut(row.value) : row.value, 'left');
        },
        minWidth: 60,
        Filter: createFilterInput('rut__icontains', 'Filtrar RUT, mínimo 3 caracteres', 3),
      },
      {
        Header: headerOperation('País', 'center'),
        id: 'pais__id',
        accessor: 'pais',
        Cell: (row) => cellOperation(row.value?.name || 'Chile', 'left'),
        // eslint-disable-next-line react/prop-types
        Filter: () => (
          <FilterSelectWrapper
            columnId="pais__id"
            options={paisesOptions}
            defaultOption={{ label: 'Todos', value: NO_FILTERED_ID }}
            onChange={handleFilterChange}
            clearSignal={clearSignal}
          />
        ),
        minWidth: 60,
        sortable: false,
      },
      {
        Header: headerOperation('Nombre', 'center'),
        id: 'name__icontains',
        accessor: 'name',
        Cell: (row) => cellOperation(row.value, 'left'),
        minWidth: 170,
        Filter: createFilterInput('name__icontains', 'Filtrar Nombre, mínimo 3 caracteres', 3),
      },
      {
        Header: headerOperation('Acciones', 'center'),
        id: 'acciones',
        accessor: 'id',
        Cell: (row) =>
          cellOperation(
            <Link to={`/posicion-deudor/${row.value}`} className="table-link">
              <FontAwesomeIcon icon={faEye} />
            </Link>,
          ),
        minWidth: 100,
        filterable: false,
        sortable: false,
      },
    ];
  }, [clearSignal, paisesOptions]);

  const isValidFilterValue = (id, value) => {
    if (value === null || value === undefined) return false;
    
    if (typeof value === 'string') {
      if (value.trim() === '') return false;
      // Check minimum length for specific text filters
      if (id === 'rut__icontains' && value.trim().length < 3) return false;
      if (id === 'name__icontains' && value.trim().length < 3) return false;
    }
    
    // Numbers (like pais__id) are valid as-is
    return true;
  };

  const isEnteredFilterValue = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    return true;
  };

  const handleApplyFilters = () => {
    const filterValues = filterValuesRef.current;
    
    const filtered = Object.entries(filterValues)
      .filter(([id, value]) => isValidFilterValue(id, value))
      .map(([id, value]) => ({ id, value }));
    
    setActiveFilters(Object.entries(filterValues)
      .filter(([_, value]) => isEnteredFilterValue(value))
      .map(([id, value]) => ({ id, value })));
    
    setFilterCount(filtered.length);
    
    const newTableState = {
      ...tableStateRef.current,
      filtered,
      page: 0,
    };
    
    onFetchData(newTableState);
  };

  const handleClearFilters = () => {
    filterValuesRef.current = {};
    setActiveFilters([]);
    setFilterCount(0);

    setClearSignal(prev => prev + 1);

    const newTableState = {
      ...tableStateRef.current,
      filtered: [],
      page: 0,
    };

    onFetchData(newTableState);
  };

  const handleFetchData = (tableState) => {
    tableStateRef.current = tableState;
    
    if (filterCount > 0 && !isInitialMount.current) {
      const newTableState = {
        ...tableState,
        filtered: activeFilters.filter(filter => {
          const value = filter.value || '';
          if (filter.id === 'rut__icontains' && value.trim().length < 3) return false;
          if (filter.id === 'name__icontains' && value.trim().length < 3) return false;
          return true;
        }),
      };
      return onFetchData(newTableState);
    }
    
    isInitialMount.current = false;
    return onFetchData(tableState);
  };

  return (
    <div style={{ margin: '25px 0 40px 0' }}>
      <div style={{ marginBottom: '15px', display: 'flex', gap: '10px', alignItems: 'center' }}>
        <Button
          onClick={handleApplyFilters}
          style={{
            padding: '8px 16px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <FontAwesomeIcon icon={faFilter} />
          Aplicar Filtro
        </Button>
        
        <Button
          onClick={handleClearFilters}
          style={{
            padding: '8px 16px',
            backgroundColor: activeFilters.length > 0 ? '#dc3545' : '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: activeFilters.length > 0 ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
          disabled={activeFilters.length === 0}
        >
          <FontAwesomeIcon icon={faTimes} />
          Limpiar Filtros
        </Button>
        
        {activeFilters.length > 0 && (
          <div style={{ marginLeft: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: '#666', fontSize: '14px' }}>
              Filtros ingresados: {activeFilters.length}
            </span>
            {filterCount > 0 && filterCount !== activeFilters.length && (
              <span style={{ color: '#007bff', fontSize: '14px', fontWeight: 'bold' }}>
                Filtros aplicados: {filterCount}
              </span>
            )}
            {filterCount === 0 && activeFilters.length > 0 && (
              <span style={{ 
                color: '#dc3545', 
                fontSize: '14px',
                backgroundColor: '#fff5f5',
                padding: '4px 8px',
                borderRadius: '4px',
                border: '1px solid #ffcdd2'
              }}
              >
                ⚠️ Los filtros requieren al menos 3 caracteres
              </span>
            )}
          </div>
        )}
      </div>

      <BaseTable
        columns={columns}
        data={data.data}
        resizable={false}
        loading={loading}
        onFetchData={handleFetchData}
        pages={data.pages}
        resultCount={data.count}
        hasFixedColumns
        showPagination
        sortable={false}
        filterable
        style={{ maxHeight: '63vh' }}
      />
    </div>
  );
};

export default DeudorListTable;
